import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { TextField, FieldLabel } from '../ui/Field'
import { DateTime } from 'luxon'
import { DateTimePickerField } from '../ui/DateTimePickerField'
import { parseDateTimeInput, formatDateTimeInput } from '../ui/dateTimeInput'
import { extractApiError } from '../../lib/api'
import { softwareWatchApi, parseIpList } from '../../lib/software-watch-api'
import { ScopeSelector } from './ScopeSelector'

/** Titled form section (module-level so it isn't recreated each render). */
function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-ink-muted)]">{title}</h4>
      {children}
    </div>
  )
}

/**
 * Create or edit a data-platform consumer.
 *
 * CREATE ("Add Data Platform"): platform metadata + the first SCOPED key
 * (scopes / allowed IPs / expiry) in one step — the backend mints the key and
 * returns the raw value once (revealed via `onCreated`).
 * EDIT: platform metadata (name / status) + the key's scopes /
 * allowed IPs / expiry, edited in place.
 */
export function ConsumerFormDialog({ initial, onClose, onCreated }) {
  const qc = useQueryClient()
  const isEdit = Boolean(initial)
  // A platform is created with exactly one key, so Edit works on that key —
  // its scopes / IPs / expiry are edited inline here. Prefer the first live
  // key, fall back to the first key. Null in the create flow.
  const editKey = isEdit
    ? (initial.keys?.find((k) => !k.revokedAt) ?? initial.keys?.[0] ?? null)
    : null

  const [form, setForm] = useState({
    name: initial?.name ?? '',
    enabled: initial ? initial.enabled : true,
  })
  const [scopes, setScopes] = useState(editKey?.scopes ?? [])
  const [allowedIps, setAllowedIps] = useState((editKey?.allowedIps ?? []).join(', '))
  const initialExpiry = formatDateTimeInput(editKey?.expiresAt)
  const [expiry, setExpiry] = useState(initialExpiry)
  const [errors, setErrors] = useState({})

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  function validate() {
    const next = {}
    const trimmedName = form.name.trim()
    if (trimmedName.length === 0) {
      next.name = 'Data Platform Name is required.'
    } else if (trimmedName.length < 2) {
      next.name = 'Data Platform Name must be at least 2 characters.'
    }
    const expiryValue = expiry.trim()
    if (expiryValue) {
      const parsedExpiry = parseDateTimeInput(expiryValue)
      if (!parsedExpiry) {
        next.expiry = 'Please enter a valid date and time, or leave it empty.'
      } else if (
        parsedExpiry < DateTime.local().startOf('minute') &&
        expiryValue !== initialExpiry.trim()
      ) {
        // Expiry must be the present/future — a past value would mint an
        // already-dead key. An UNCHANGED pre-existing past expiry (editing an
        // already-expired key) is left alone so its other fields can still save.
        next.expiry = 'Expiry must be a future date and time.'
      }
    }
    return next
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const meta = {
        name: form.name.trim(),
        enabled: form.enabled,
      }
      const expiresAt = expiry.trim()
        ? parseDateTimeInput(expiry).toJSDate().toISOString()
        : null
      if (isEdit) {
        await softwareWatchApi.updateConsumer(initial._id, meta)
        // Save the key's access config in the same step (single-key platform).
        if (editKey) {
          await softwareWatchApi.updateKey(initial._id, editKey._id, {
            scopes,
            allowedIps: parseIpList(allowedIps),
            expiresAt,
          })
        }
        return null
      }
      return softwareWatchApi.createConsumer({
        ...meta,
        scopes,
        allowedIps: parseIpList(allowedIps),
        expiresAt,
      })
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      toast.success(
        isEdit ? 'Data platform updated successfully.' : 'Data platform created successfully.',
      )
      if (!isEdit && result?.raw) {
        onCreated(result.raw, (result?.key?.scopes?.length ?? 0) === 0)
      }
      onClose()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) setErrors((prev) => ({ ...prev, ...fields }))
      else toast.error(message)
    },
  })

  function submit(e) {
    e.preventDefault()
    if (mutation.isPending) return
    const next = validate()
    setErrors(next)
    if (Object.keys(next).length === 0) mutation.mutate()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Data Platform' : 'Add Data Platform'}
      description="Name the platform and choose exactly which data its key can pull."
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button form="sw-consumer-form" type="submit" loading={mutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create & Generate Key'}
          </Button>
        </>
      }
    >
      <form id="sw-consumer-form" noValidate className="space-y-5" onSubmit={submit}>
        {/* Basic */}
        <Section title="Basic information">
          {/* Placeholder mirrors the label ("Enter <Label>") per the house
           *  form convention — it read "e.g. SoftwareWatch, Analytics-BI",
           *  which described the field differently from its own label. The
           *  examples weren't lost: they moved to helper text, which is
           *  where guidance belongs and, unlike a placeholder, stays
           *  visible once the user starts typing. */}
          <TextField
            label="Data Platform Name"
            required
            maxLength={200}
            placeholder="Enter a Data Platform Name"
            helperText="For example: SoftwareWatch, Analytics-BI."
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={errors.name}
          />
          <div>
            <FieldLabel label="Status" />
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-[color:var(--color-rule-strong)] accent-[color:var(--color-primary)] cursor-pointer"
                checked={form.enabled}
                onChange={(e) => setField('enabled', e.target.checked)}
              />
              <span className="text-sm text-[color:var(--color-ink)]">
                {form.enabled ? 'Active' : 'Disabled — all its keys are rejected'}
              </span>
            </label>
          </div>
        </Section>

        {/* Access — the platform's API key. Its scopes / IPs / expiry are set
            on create (the first key) and edited here in place afterwards. */}
        <Section title={isEdit ? 'Access — API key' : 'Access — the first API key'}>
          <div>
            <FieldLabel label="What Data Can This Platform Access?" />
            <ScopeSelector value={scopes} onChange={setScopes} />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Grant the least access this key needs. No scopes = discovery endpoints only.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Same convention. The address format that used to live in the
             *  placeholder is folded into the existing helper text, so no
             *  guidance is lost. */}
            <TextField
              label="Allowed IPs"
              placeholder="Enter the Allowed IPs"
              value={allowedIps}
              onChange={(e) => setAllowedIps(e.target.value)}
              helperText="Comma or space separated, for example 203.0.113.10, 10.0.0.0/8. Empty = any IP."
            />
            <DateTimePickerField
              label="Expiry Date & Time"
              value={expiry}
              onChange={setExpiry}
              error={errors.expiry}
              minDate={DateTime.local()}
            />
          </div>
        </Section>
      </form>
    </Modal>
  )
}
